import { ReactElement, useEffect, useState, useMemo, useCallback } from 'react';

import { Timestamp } from 'firebase/firestore';

import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import AcUnitIcon from '@mui/icons-material/AcUnit';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

import { useData } from '../../contextProviders/DataProvider';
import useNotices from '../../dataHooks/useNotices';
import { ActionNotice } from '../../types/ActionNotice';
import { NOTICE_TYPES } from '../../types/Notice';
import { DisplayDate } from '../../utils/utils';

/** Unified item for the banner carousel */
interface BannerItem {
  id: string;
  title: string;
  text: string;
  icon: ReactElement;
  link: string;
  action?: {
    label: string;
    onAction: () => void;
  };
}

interface GratiNoticeProps {
  actionNotices?: ActionNotice[];
  sx?: SxProps<Theme>;
}

function getFirestoreNoticeIcon(noticeType: string): ReactElement {
  switch (noticeType) {
    case NOTICE_TYPES.ANNOUNCEMENT:
      return <InfoIcon />;
    case NOTICE_TYPES.ALERT:
      return <WarningIcon />;
    case NOTICE_TYPES.CALENDAR:
      return <CalendarMonthIcon />;
    case NOTICE_TYPES.ERROR:
      return <ErrorIcon />;
    case NOTICE_TYPES.FROSTDELAY:
      return <AcUnitIcon />;
    default:
      return <InfoIcon />;
  }
}

export default function GratiNoticeBanner(props: GratiNoticeProps) {
  const { actionNotices = [], sx } = props;
  const { userState } = useData();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
  const [noticeVisible, setNoticeVisible] = useState<('none' | 'sliding' | 'block')[]>([]);

  const { notices: allNotices, isNoticesLoading, noticesError } = useNotices(
    userState.status === 'ready' ? { contextPairs: null, filter: 'all' } : {}
  );

  // Filter to only active Firestore notices
  const firestoreNotices = useMemo(() => {
    if (!allNotices) return [];
    const now = new Date();
    return allNotices.filter((notice) => {
      const startDate = (notice.displayStartsAt as Timestamp).toDate();
      const endDate = (notice.displayStopsAt as Timestamp).toDate();
      return now >= startDate && now <= endDate;
    });
  }, [allNotices]);

  // Build unified banner items: action notices first, then Firestore notices
  const bannerItems: BannerItem[] = useMemo(() => {
    const items: BannerItem[] = [];

    for (const an of actionNotices) {
      const IconComponent = an.icon;
      items.push({
        id: an.id,
        title: an.title,
        text: an.text,
        icon: IconComponent ? <IconComponent /> : <WarningIcon />,
        link: an.link,
        action: an.actionLabel && an.onAction
          ? { label: an.actionLabel, onAction: an.onAction }
          : undefined,
      });
    }

    for (const notice of firestoreNotices) {
      const titlePrefix = ([NOTICE_TYPES.CALENDAR, NOTICE_TYPES.FROSTDELAY] as string[]).includes(notice.type)
        ? `${DisplayDate((notice.displayStopsAt as Timestamp).toDate(), 'America/Los_Angeles')}: `
        : '';
      items.push({
        id: notice.itemId,
        title: `${titlePrefix}${notice.title}`,
        text: notice.text,
        icon: getFirestoreNoticeIcon(notice.type),
        link: '/notices',
      });
    }

    return items;
  }, [actionNotices, firestoreNotices]);

  // Reset carousel state when items change
  useEffect(() => {
    if (bannerItems.length > 0) {
      setNoticeVisible(['block', ...Array(bannerItems.length - 1).fill('none')]);
      setCurrentIndex(0);
    }
  }, [bannerItems.length]);

  useEffect(() => {
    if (isSliding === false) {
      setSlideDirection((prev) => (prev === 'left' ? 'right' : 'left'));
      setNoticeVisible((prev) => {
        const newNoticeVisible = [...prev];
        newNoticeVisible[currentIndex] = 'block';
        return newNoticeVisible;
      });
    }
  }, [isSliding, currentIndex]);

  const handleNext = useMemo(() => () => {
    if (bannerItems.length === 0) return;
    setSlideDirection('left');
    setIsSliding(true);
    setCurrentIndex((prev) => {
      const newIndex = (prev + 1) % bannerItems.length;
      setNoticeVisible((prevNotices) => {
        const newNoticeVisible = [...prevNotices];
        newNoticeVisible[prev] = 'sliding';
        return newNoticeVisible;
      });
      return newIndex;
    });
  }, [bannerItems.length]);

  const handlePrev = useMemo(() => () => {
    if (bannerItems.length === 0) return;
    setSlideDirection('right');
    setIsSliding(true);
    setCurrentIndex((prev) => {
      const newIndex = (prev - 1 + bannerItems.length) % bannerItems.length;
      setNoticeVisible((prevNotices) => {
        const newNoticeVisible = [...prevNotices];
        newNoticeVisible[prev] = 'sliding';
        return newNoticeVisible;
      });
      return newIndex;
    });
  }, [bannerItems.length]);

  const handleExited = useCallback((index: number) => {
    setIsSliding(false);
    setNoticeVisible((prevNotices) => {
      const newNoticeVisible = [...prevNotices];
      newNoticeVisible[index] = 'none';
      return newNoticeVisible;
    });
  }, []);

  const handleBannerClick = useCallback((link: string) => {
    navigate(link);
  }, [navigate]);

  if (isNoticesLoading && actionNotices.length === 0) {
    return <></>;
  }

  if (noticesError && actionNotices.length === 0) {
    return <></>;
  }

  if (bannerItems.length === 0) return <></>;

  return (
    <Box
      sx={{
        ...sx,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        pr: bannerItems.length <= 1 ? 1 : 0,
      }}
    >
      {bannerItems.length > 1 && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          sx={{ color: 'primary.onContainer', flexShrink: 0 }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      {bannerItems.map((item, index) => (
        <Box
          key={item.id}
          sx={{
            display: noticeVisible[index] === 'none' ? 'none' : 'flex',
            flex: 1,
            minWidth: 0,
            height: '100%',
          }}
          onClick={() => handleBannerClick(item.link)}
        >
          <Slide
            direction={slideDirection}
            in={noticeVisible[index] === 'block'}
            timeout={500}
            onExited={() => handleExited(index)}
          >
            <Alert
              icon={item.icon}
              severity="info"
              action={item.action && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    item.action!.onAction();
                  }}
                  sx={{
                    color: 'primary.onContainer',
                    borderColor: 'primary.onContainer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.action.label}
                </Button>
              )}
              sx={{
                display: 'flex',
                flexGrow: 1,
                py: 0,
                pr: item.action ? 1 : 0,
                mr: 0,
                width: '100%',
                backgroundColor: 'transparent',
                color: 'primary.onContainer',
                '& .MuiAlert-icon': {
                  color: 'primary.onContainer',
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center'
                },
                '& .MuiAlert-action': {
                  alignItems: 'center',
                  pt: 0,
                },
                '& .MuiAlert-message': {
                  overflow: 'hidden',
                  minWidth: 0,
                },
                position: 'relative',
              }}
            >
              <AlertTitle
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {item.title}
              </AlertTitle>
              <Typography
                variant="bodyMedium"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {item.text}
              </Typography>
            </Alert>
          </Slide>
        </Box>
      ))}

      {bannerItems.length > 1 && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          sx={{ color: 'primary.onContainer', flexShrink: 0 }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  );
}
